import { format } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { AiOutlineClose, AiOutlineExport, AiOutlineReload } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import { toasting } from 'src/lib/components/Toast.component';
import { parsePublishTime } from 'src/lib/date/parse-publish-time';
import { fget, fpost } from 'src/lib/fetch';
import {
  closeAllMapDevicePanels,
  closeMapDevicePanel,
  MapDevicePanel,
} from 'src/redux/slices/mapDevicePanels.slice';
import { RootState } from 'src/redux/store';
import './WaterLevel.css';
import { recentTimeRange } from './recentTimeRange';
import { AlertDevice, configuredAlerts } from './configuredAlerts';

type TrendRange = 'year' | 'month' | 'week' | 'day';
type SeriesKey = 'flowRate' | 'waterLevel' | 'velocity';
type ResizeDirection = 'top' | 'right' | 'bottom' | 'left' | 'topLeft' | 'topRight' | 'bottomRight' | 'bottomLeft';
type RawItem = Record<string, { S?: string; N?: string } | undefined>;

const RESIZE_DIRECTIONS: ResizeDirection[] = [
  'top',
  'right',
  'bottom',
  'left',
  'topLeft',
  'topRight',
  'bottomRight',
  'bottomLeft',
];
const PANEL_MIN_WIDTH = 520;
const PANEL_MIN_HEIGHT = 480;
const RAW_DATA_PAGE_SIZE = 5;
const compactMapPanelMediaQuery = '(max-width: 1024px), (hover: none) and (pointer: coarse)';

interface TrendPoint {
  timestamp: number;
  label: string;
  flowRate: number | null;
  waterLevel: number | null;
  velocity: number | null;
}

const RANGE_OPTIONS: Array<{ key: TrendRange; label: string }> = [
  { key: 'year', label: '年' },
  { key: 'month', label: '月' },
  { key: 'week', label: '週' },
  { key: 'day', label: '日' },
];

const SERIES: Array<{ key: SeriesKey; label: string; color: string; unit: string }> = [
  { key: 'flowRate', label: '流量', color: '#015cac', unit: 'm³/s' },
  { key: 'waterLevel', label: '水位', color: '#ff1730', unit: 'm' },
  { key: 'velocity', label: '流速', color: '#16a34a', unit: 'm/s' },
];

const TABLE_BY_RANGE: Record<TrendRange, string> = {
  year: 'DEVICE_RECORD_YEAR',
  month: 'DEVICE_RECORD_MONTH',
  week: 'DEVICE_RECORD_MONTH',
  day: 'DEVICE_RECORD_DAY',
};

const X_AXIS_LABEL: Record<TrendRange, string> = {
  year: '月次推移',
  month: '日次推移',
  week: '日次推移',
  day: '時間推移',
};

const readAttribute = (item: RawItem, key: string) => item[key]?.S ?? item[key]?.N ?? '';

const toNumber = (value?: string) => {
  if (!value?.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toOptionalNumber = (value: string) => {
  if (!value.trim()) return null;
  return toNumber(value);
};

const getMetricValueSizeClass = (value: string) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';

  const integerDigits = Math.trunc(Math.abs(numericValue)).toString().length;
  if (integerDigits >= 5 || (numericValue < 0 && integerDigits >= 4)) return 'is-extra-compact';
  if (integerDigits >= 4) return 'is-compact';
  return '';
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const endOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const getPresetRange = (range: TrendRange, anchor: Date) => {
  if (range === 'year') {
    return {
      start: new Date(anchor.getFullYear(), anchor.getMonth() - 11, 1),
      end: new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 23, 59, 59, 999),
    };
  }

  if (range === 'month') {
    const start = startOfDay(anchor);
    start.setDate(start.getDate() - 30);
    return { start, end: endOfDay(anchor) };
  }

  if (range === 'week') {
    const start = startOfDay(anchor);
    start.setDate(start.getDate() - 6);
    return { start, end: endOfDay(anchor) };
  }

  return { start: startOfDay(anchor), end: endOfDay(anchor) };
};

const toDateTimeInput = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm");

const parseDateTimeInput = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getItemDate = (item: RawItem) => {
  const createAt = readAttribute(item, 'CREATE_AT');
  if (createAt) {
    const date = new Date(Number(createAt));
    if (!Number.isNaN(date.getTime())) return date;
  }

  return parsePublishTime(
    readAttribute(item, 'PUBLISH_FORMATED') ||
      readAttribute(item, 'PUBLISH') ||
      readAttribute(item, 'Publish'),
  );
};

const buildPointLabel = (range: TrendRange, date: Date, spansMultipleDays: boolean) => {
  if (range === 'year') return format(date, 'yyyy/MM');
  if (range === 'month' || range === 'week') return format(date, 'M/d');
  return format(date, spansMultipleDays ? 'M/d HH:mm' : 'HH:mm');
};

const aggregateTrendPoints = (
  source: TrendPoint[],
  range: 'week' | 'day',
  spansMultipleDays: boolean,
) => {
  const buckets = new Map<
    number,
    Record<SeriesKey, { total: number; count: number }>
  >();

  source.forEach((point) => {
    const date = new Date(point.timestamp);
    if (range === 'week') {
      date.setHours(0, 0, 0, 0);
    } else {
      date.setMinutes(0, 0, 0);
    }
    const timestamp = date.getTime();
    const bucket = buckets.get(timestamp) ?? {
      flowRate: { total: 0, count: 0 },
      waterLevel: { total: 0, count: 0 },
      velocity: { total: 0, count: 0 },
    };

    SERIES.forEach(({ key }) => {
      const value = point[key];
      if (value === null || !Number.isFinite(value)) return;
      bucket[key].total += value;
      bucket[key].count += 1;
    });
    buckets.set(timestamp, bucket);
  });

  return [...buckets.entries()]
    .map(([timestamp, bucket]) => {
      const average = (key: SeriesKey) =>
        bucket[key].count ? bucket[key].total / bucket[key].count : null;
      return {
        timestamp,
        label: buildPointLabel(range, new Date(timestamp), spansMultipleDays),
        flowRate: average('flowRate'),
        waterLevel: average('waterLevel'),
        velocity: average('velocity'),
      } as TrendPoint;
    })
    .sort((left, right) => left.timestamp - right.timestamp);
};

const fillTrendPointBuckets = (
  source: TrendPoint[],
  range: 'week' | 'day',
  start: Date,
  end: Date,
  spansMultipleDays: boolean,
) => {
  if (!source.length) return source;

  const bucketStart = (timestamp: number) => {
    const date = new Date(timestamp);
    if (range === 'week') {
      date.setHours(0, 0, 0, 0);
    } else {
      date.setMinutes(0, 0, 0);
    }
    return date;
  };
  const pointsByBucket = new Map(
    source.map((point) => [bucketStart(point.timestamp).getTime(), point]),
  );
  const cursor = bucketStart(start.getTime());
  const lastBucket = bucketStart(end.getTime()).getTime();
  const result: TrendPoint[] = [];

  while (cursor.getTime() <= lastBucket) {
    const timestamp = cursor.getTime();
    const point = pointsByBucket.get(timestamp);
    result.push({
      timestamp,
      label: buildPointLabel(range, cursor, spansMultipleDays),
      flowRate: point?.flowRate ?? null,
      waterLevel: point?.waterLevel ?? null,
      velocity: point?.velocity ?? null,
    });

    if (range === 'week') {
      cursor.setDate(cursor.getDate() + 1);
    } else {
      cursor.setHours(cursor.getHours() + 1);
    }
  }

  return result;
};

const buildAxisRange = (values: Array<number | null>, customMin: string, customMax: string) => {
  const validValues = values.filter((value): value is number => value !== null && Number.isFinite(value));
  let min = validValues.length ? Math.min(...validValues) : 0;
  let max = validValues.length ? Math.max(...validValues) : 1;

  if (min === max) {
    const padding = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 1;
    min -= padding;
    max += padding;
  } else {
    const padding = (max - min) * 0.08;
    min -= padding;
    max += padding;
  }

  min = toOptionalNumber(customMin) ?? min;
  max = toOptionalNumber(customMax) ?? max;
  if (min > max) [min, max] = [max, min];
  if (min === max) max = min + 1;

  return { min, max };
};

const csvEscape = (value: string) => `"${value.replace(/"/g, '""')}"`;

export function DeviceDetailPanel({
  dataDialog,
  onClose,
  isActive = false,
  onActivate = () => undefined,
  layoutMode = 'floating',
  showKpi = true,
}: {
  dataDialog: MapDevicePanel;
  onClose?: () => void;
  isActive?: boolean;
  onActivate?: () => void;
  layoutMode?: 'floating' | 'embedded';
  showKpi?: boolean;
}) {
  const initialRange = getPresetRange('day', new Date());

  const [activeRange, setActiveRange] = useState<TrendRange>('day');
  const [quickRangeMinutes, setQuickRangeMinutes] = useState<number | null>(null);
  const [startDraft, setStartDraft] = useState(toDateTimeInput(initialRange.start));
  const [endDraft, setEndDraft] = useState(toDateTimeInput(initialRange.end));
  const [appliedStart, setAppliedStart] = useState(initialRange.start);
  const [appliedEnd, setAppliedEnd] = useState(initialRange.end);
  const [points, setPoints] = useState<TrendPoint[]>([]);
  const [rawRows, setRawRows] = useState<TrendPoint[]>([]);
  const [rawRowLimit, setRawRowLimit] = useState(RAW_DATA_PAGE_SIZE);
  const [hasMoreRawRows, setHasMoreRawRows] = useState(false);
  const [isLoadingMoreRawRows, setIsLoadingMoreRawRows] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [alertDevice, setAlertDevice] = useState<AlertDevice | null>(null);
  const [alertCheckedAt, setAlertCheckedAt] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    const update = async () => {
      try {
        const response = await fget<unknown, {Item?: AlertDevice}>(`api/devices/${encodeURIComponent(dataDialog.deviceId)}`);
        if (!cancelled) {
          setAlertDevice(response?.[1]?.Item ?? null);
          setAlertCheckedAt(Date.now());
        }
      } catch {
        if (!cancelled) setAlertDevice(null);
      }
    };
    setAlertDevice(null);
    void update();
    const timer = window.setInterval(() => void update(), 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [dataDialog.deviceId]);
  const [rangeError, setRangeError] = useState('');
  const [showAxisEditor, setShowAxisEditor] = useState(false);
  const [hiddenSeries, setHiddenSeries] = useState<Record<SeriesKey, boolean>>({
    flowRate: false,
    waterLevel: false,
    velocity: false,
  });
  const [axisInputs, setAxisInputs] = useState<Record<SeriesKey, { min: string; max: string }>>({
    flowRate: { min: '', max: '' },
    waterLevel: { min: '', max: '' },
    velocity: { min: '', max: '' },
  });
  const [panelOffset, setPanelOffset] = useState({ x: 0, y: 0 });
  const [panelSize, setPanelSize] = useState<{ width: number; height: number } | null>(null);
  const panelRef = useRef<HTMLElement>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    rect: DOMRect;
    boundsRect: DOMRect;
  } | null>(null);
  const resizeStateRef = useRef<{
    pointerId: number;
    direction: ResizeDirection;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    originX: number;
    originY: number;
    rect: DOMRect;
    boundsRect: DOMRect;
  } | null>(null);
  const requestIdRef = useRef(0);

  const startDragging = (event: React.PointerEvent<HTMLElement>) => {
    if (layoutMode !== 'floating') return;
    if (window.matchMedia(compactMapPanelMediaQuery).matches) return;
    if ((event.target as HTMLElement).closest('button, input, a')) return;
    const panel = panelRef.current;
    if (!panel) return;

    onActivate();
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: panelOffset.x,
      originY: panelOffset.y,
      rect: panel.getBoundingClientRect(),
      boundsRect:
        panel.closest('.device-detail-overlay')?.getBoundingClientRect() ??
        new DOMRect(0, 0, window.innerWidth, window.innerHeight),
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const movePanel = (event: React.PointerEvent<HTMLElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const minX = dragState.originX + dragState.boundsRect.left - dragState.rect.left;
    const maxX = dragState.originX + dragState.boundsRect.right - 80 - dragState.rect.left;
    const minY = dragState.originY + dragState.boundsRect.top - dragState.rect.top;
    const maxY = dragState.originY + dragState.boundsRect.bottom - 52 - dragState.rect.top;
    const nextX = dragState.originX + event.clientX - dragState.startX;
    const nextY = dragState.originY + event.clientY - dragState.startY;

    setPanelOffset({
      x: Math.min(Math.max(nextX, minX), Math.max(minX, maxX)),
      y: Math.min(Math.max(nextY, minY), Math.max(minY, maxY)),
    });
  };

  const stopDragging = (event: React.PointerEvent<HTMLElement>) => {
    if (dragStateRef.current?.pointerId !== event.pointerId) return;
    dragStateRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const startResizing = (direction: ResizeDirection) => (event: React.PointerEvent<HTMLElement>) => {
    if (layoutMode !== 'floating') return;
    if (window.matchMedia(compactMapPanelMediaQuery).matches) return;
    const panel = panelRef.current;
    if (!panel) return;

    const rect = panel.getBoundingClientRect();
    onActivate();
    resizeStateRef.current = {
      pointerId: event.pointerId,
      direction,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
      originX: panelOffset.x,
      originY: panelOffset.y,
      rect,
      boundsRect:
        panel.closest('.device-detail-overlay')?.getBoundingClientRect() ??
        new DOMRect(0, 0, window.innerWidth, window.innerHeight),
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.stopPropagation();
    event.preventDefault();
  };

  const resizePanel = (event: React.PointerEvent<HTMLElement>) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState || resizeState.pointerId !== event.pointerId) return;

    const { direction, rect, boundsRect } = resizeState;
    const deltaX = event.clientX - resizeState.startX;
    const deltaY = event.clientY - resizeState.startY;
    const resizesLeft = direction.includes('Left') || direction === 'left';
    const resizesRight = direction.includes('Right') || direction === 'right';
    const resizesTop = direction.includes('top') || direction === 'top';
    const resizesBottom = direction.includes('bottom') || direction === 'bottom';

    let width = resizeState.startWidth;
    let height = resizeState.startHeight;
    let x = resizeState.originX;
    let y = resizeState.originY;

    if (resizesRight) {
      const maxWidth = boundsRect.right - rect.left;
      width = Math.min(Math.max(resizeState.startWidth + deltaX, Math.min(PANEL_MIN_WIDTH, maxWidth)), maxWidth);
    } else if (resizesLeft) {
      const maxWidth = rect.right - boundsRect.left;
      width = Math.min(Math.max(resizeState.startWidth - deltaX, Math.min(PANEL_MIN_WIDTH, maxWidth)), maxWidth);
      x = resizeState.originX + resizeState.startWidth - width;
    }

    if (resizesBottom) {
      const maxHeight = boundsRect.bottom - rect.top;
      height = Math.min(Math.max(resizeState.startHeight + deltaY, Math.min(PANEL_MIN_HEIGHT, maxHeight)), maxHeight);
    } else if (resizesTop) {
      const maxHeight = rect.bottom - boundsRect.top;
      height = Math.min(Math.max(resizeState.startHeight - deltaY, Math.min(PANEL_MIN_HEIGHT, maxHeight)), maxHeight);
      y = resizeState.originY + resizeState.startHeight - height;
    }

    setPanelSize({ width, height });
    setPanelOffset({ x, y });
    event.preventDefault();
  };

  const stopResizing = (event: React.PointerEvent<HTMLElement>) => {
    if (resizeStateRef.current?.pointerId !== event.pointerId) return;
    resizeStateRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const queryTable = async (
    tableName: string,
    start: Date,
    end: Date,
    options: { scanIndexForward?: boolean; limit?: number } = {},
  ) => {
    if (!dataDialog.deviceId) return [];
    const response = await fpost<any>('api/device/record/query', {
      body: {
        query: {
          ExpressionAttributeValues: {
            ':id': { S: dataDialog.deviceId },
            ':timeStart': { S: String(start.getTime()) },
            ':timeEnd': { S: String(end.getTime()) },
          },
          KeyConditionExpression: 'DEVICE_ID = :id and CREATE_AT BETWEEN :timeStart and :timeEnd',
          TableName: tableName,
          ScanIndexForward: options.scanIndexForward ?? true,
          ...(options.limit ? { Limit: options.limit } : {}),
        },
      },
    });
    return (response?.[1]?.Items as RawItem[]) ?? [];
  };

  const queryRawRecords = async (start: Date, end: Date) => {
    if (!dataDialog.deviceId) return { items: [] as RawItem[], hasMore: false };
    const buildQuery = (dateFormat: string) => ({
      query: {
        ExpressionAttributeValues: {
          ':id': { S: dataDialog.deviceId },
          ':timeStart': { S: format(start, dateFormat) },
          ':timeEnd': { S: format(end, dateFormat) },
        },
        KeyConditionExpression: 'DEVICE_ID = :id and PUBLISH_FORMATED BETWEEN :timeStart and :timeEnd',
        TableName: 'DEVICE_RECORD',
        Select: 'ALL_ATTRIBUTES',
        IndexName: 'DEVICE_ID-PUBLISH_FORMATED-index',
        ScanIndexForward: true,
      },
    });

    const response = await fpost<any>('api/device/record/query', { body: buildQuery('yyyy/MM/dd HH:mm:ss') });
    const items = (response?.[1]?.Items as RawItem[]) ?? [];
    const hasMore = Object.keys(response?.[1]?.LastEvaluatedKey ?? {}).length > 0;
    if (hasMore || dataDialog.deviceId !== 'DEVICE_1721980272836') return { items, hasMore };

    const fallback = await fpost<any>('api/device/record/query', { body: buildQuery('yyyy/M/dd HH:mm:ss') });
    return {
      items: [...items, ...(((fallback?.[1]?.Items as RawItem[]) ?? []))],
      hasMore: Object.keys(fallback?.[1]?.LastEvaluatedKey ?? {}).length > 0,
    };
  };

  const queryRawRows = async (limit: number) => {
    if (!dataDialog.deviceId) return { items: [] as RawItem[], hasMore: false };
    const response = await fpost<any>('api/device/record/query', {
      body: {
        query: {
          ExpressionAttributeValues: {
            ':id': { S: dataDialog.deviceId },
          },
          KeyConditionExpression: 'DEVICE_ID = :id',
          TableName: 'DEVICE_RECORD',
          ScanIndexForward: false,
          Limit: limit,
        },
      },
    });
    return {
      items: (response?.[1]?.Items as RawItem[]) ?? [],
      hasMore: Boolean(response?.[1]?.LastEvaluatedKey),
    };
  };

  const toTrendPoint = (item: RawItem) => {
    const date = getItemDate(item);
    if (!date) return null;
    const spansMultipleDays = format(appliedStart, 'yyyy-MM-dd') !== format(appliedEnd, 'yyyy-MM-dd');
    return {
      timestamp: date.getTime(),
      label: buildPointLabel(activeRange, date, spansMultipleDays),
      flowRate: toNumber(readAttribute(item, 'FLOW_RATE')),
      waterLevel: toNumber(readAttribute(item, 'WATER_LEVEL')),
      velocity: toNumber(readAttribute(item, 'VELOCITY')),
    } as TrendPoint;
  };

  const buildRawRows = (items: RawItem[]) => items
    .map(toTrendPoint)
    .filter((point): point is TrendPoint => point !== null)
    .sort((left, right) => right.timestamp - left.timestamp);

  const loadTrend = async (silent = false) => {
    if (!dataDialog.deviceId) return;
    const requestId = ++requestIdRef.current;
    if (!silent) setIsLoading(true);

    try {
      const tableName = quickRangeMinutes === null ? TABLE_BY_RANGE[activeRange] : 'DEVICE_RECORD';
      const rawRowsPromise = queryRawRows(rawRowLimit).catch((error) => {
        console.error(error);
        return { items: [] as RawItem[], hasMore: false };
      });
      const items = await queryTable(tableName, appliedStart, appliedEnd);
      const isWithinAppliedRange = (point: TrendPoint) =>
        point.timestamp >= appliedStart.getTime() && point.timestamp <= appliedEnd.getTime();
      let nextPoints = items
        .map(toTrendPoint)
        .filter((point): point is TrendPoint => point !== null)
        .filter(isWithinAppliedRange)
        .sort((left, right) => left.timestamp - right.timestamp);

      if (quickRangeMinutes === null && !nextPoints.length && (activeRange === 'week' || activeRange === 'day')) {
        const fallbackTables = activeRange === 'week'
          ? ['DEVICE_RECORD_DAY', 'DEVICE_RECORD']
          : ['DEVICE_RECORD'];
        const spansMultipleDays =
          format(appliedStart, 'yyyy-MM-dd') !== format(appliedEnd, 'yyyy-MM-dd');

        for (const fallbackTable of fallbackTables) {
          const fallbackItems = await queryTable(fallbackTable, appliedStart, appliedEnd);
          const fallbackPoints = fallbackItems
            .map(toTrendPoint)
            .filter((point): point is TrendPoint => point !== null)
            .filter(isWithinAppliedRange);
          if (!fallbackPoints.length) continue;

          nextPoints = aggregateTrendPoints(fallbackPoints, activeRange, spansMultipleDays);
          break;
        }
      }

      if (quickRangeMinutes === null && (activeRange === 'week' || activeRange === 'day')) {
        const spansMultipleDays =
          format(appliedStart, 'yyyy-MM-dd') !== format(appliedEnd, 'yyyy-MM-dd');
        nextPoints = fillTrendPointBuckets(
          nextPoints,
          activeRange,
          appliedStart,
          appliedEnd,
          spansMultipleDays,
        );
      }
      const rawPage = await rawRowsPromise;

      if (requestId === requestIdRef.current) {
        setPoints(nextPoints);
        setRawRows(buildRawRows(rawPage.items));
        setHasMoreRawRows(rawPage.hasMore);
      }
    } catch (error) {
      console.log(error);
      if (requestId === requestIdRef.current) {
        setPoints([]);
        setRawRows([]);
        setHasMoreRawRows(false);
        toasting({ children: 'グラフデータの取得に失敗しました。', containerProps: { className: 'border-red-600' } });
      }
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  const applyRange = () => {
    const nextStart = parseDateTimeInput(startDraft);
    const nextEnd = parseDateTimeInput(endDraft);
    if (!nextStart || !nextEnd) {
      setRangeError('開始日時と終了日時を入力してください。');
      return;
    }
    if (nextStart.getTime() > nextEnd.getTime()) {
      setRangeError('開始日時は終了日時より前にしてください。');
      return;
    }
    setRangeError('');
    setQuickRangeMinutes(null);
    setRawRowLimit(RAW_DATA_PAGE_SIZE);
    setHasMoreRawRows(false);
    setAppliedStart(nextStart);
    setAppliedEnd(nextEnd);
  };

  const updateDraftRange = (target: 'start' | 'end', value: string) => {
    const nextStartDraft = target === 'start' ? value : startDraft;
    const nextEndDraft = target === 'end' ? value : endDraft;

    if (target === 'start') setStartDraft(value);
    if (target === 'end') setEndDraft(value);
    setQuickRangeMinutes(null);

    const nextStart = parseDateTimeInput(nextStartDraft);
    const nextEnd = parseDateTimeInput(nextEndDraft);
    if (!nextStart || !nextEnd) return;
    if (nextStart.getTime() > nextEnd.getTime()) {
      setRangeError('開始日時は終了日時より前にしてください。');
      return;
    }

    setRangeError('');
    setRawRowLimit(RAW_DATA_PAGE_SIZE);
    setHasMoreRawRows(false);
    setAppliedStart(nextStart);
    setAppliedEnd(nextEnd);
  };

  const selectPreset = (range: TrendRange) => {
    const anchor = range === 'week' || range === 'day'
      ? new Date()
      : parsePublishTime(dataDialog.publish) ?? new Date();
    const next = getPresetRange(range, anchor);
    setActiveRange(range);
    setQuickRangeMinutes(null);
    setRawRowLimit(RAW_DATA_PAGE_SIZE);
    setHasMoreRawRows(false);
    setStartDraft(toDateTimeInput(next.start));
    setEndDraft(toDateTimeInput(next.end));
    setAppliedStart(next.start);
    setAppliedEnd(next.end);
    setRangeError('');
  };

  const selectRecentMinutes = (minutes: number) => {
    const { start, end } = recentTimeRange(minutes);

    setActiveRange('day');
    setQuickRangeMinutes(minutes);
    setRawRowLimit(RAW_DATA_PAGE_SIZE);
    setHasMoreRawRows(false);
    setStartDraft(toDateTimeInput(start));
    setEndDraft(toDateTimeInput(end));
    setAppliedStart(start);
    setAppliedEnd(end);
    setRangeError('');
  };

  const refreshTrend = (silent = false) => {
    if (quickRangeMinutes === null) {
      void loadTrend(silent);
      return;
    }
    const { start, end } = recentTimeRange(quickRangeMinutes);
    setStartDraft(toDateTimeInput(start));
    setEndDraft(toDateTimeInput(end));
    setAppliedStart(start);
    setAppliedEnd(end);
  };

  const downloadCsv = async () => {
    try {
      const { items, hasMore } = await queryRawRecords(appliedStart, appliedEnd);
      if (hasMore) {
        toasting({
          children: <>CSVデータが1MB以上のためダウンロードできません。<br />検索条件を変更してください。</>,
          containerProps: { className: 'border-red-600' },
        });
        return;
      }
      if (!items.length) {
        toasting({ children: 'CSV出力対象のデータがありません。', containerProps: { className: 'border-blue-600' } });
        return;
      }

      const headers = ['SID', 'CNT', 'RF', 'VOL', 'DIS', 'PUBLISH', 'WATER_LEVEL', 'VELOCITY', 'FLOW_RATE'];
      const rows = items.map((item) => [
        readAttribute(item, 'SID'),
        readAttribute(item, 'CNT'),
        readAttribute(item, 'RF'),
        readAttribute(item, 'VOL'),
        readAttribute(item, 'DIS'),
        readAttribute(item, 'PUBLISH_FORMATED') || readAttribute(item, 'PUBLISH') || readAttribute(item, 'Publish'),
        readAttribute(item, 'WATER_LEVEL'),
        readAttribute(item, 'VELOCITY'),
        readAttribute(item, 'FLOW_RATE'),
      ]);
      const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
      const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `trend_${dataDialog.deviceId}_${format(appliedStart, 'yyyyMMdd')}-${format(appliedEnd, 'yyyyMMdd')}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
      toasting({ children: 'CSVデータの取得に失敗しました。', containerProps: { className: 'border-red-600' } });
    }
  };

  useEffect(() => {
    if (!dataDialog.deviceId) return;
    const next = getPresetRange('day', new Date());
    setActiveRange('day');
    setQuickRangeMinutes(null);
    setStartDraft(toDateTimeInput(next.start));
    setEndDraft(toDateTimeInput(next.end));
    setAppliedStart(next.start);
    setAppliedEnd(next.end);
    setRawRows([]);
    setRawRowLimit(RAW_DATA_PAGE_SIZE);
    setHasMoreRawRows(false);
    setHiddenSeries({ flowRate: false, waterLevel: false, velocity: false });
    setAxisInputs({
      flowRate: { min: '', max: '' },
      waterLevel: { min: '', max: '' },
      velocity: { min: '', max: '' },
    });
  }, [dataDialog.deviceId]);

  useEffect(() => {
    void loadTrend();
    // loadTrend intentionally runs only after a selection or applied range changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataDialog.deviceId, activeRange, appliedStart, appliedEnd, quickRangeMinutes]);

  useEffect(() => {
    if (!dataDialog.deviceId || !autoRefresh) return;

    const intervalId = window.setInterval(() => {
      refreshTrend(true);
    }, 30000);

    return () => window.clearInterval(intervalId);
    // Recreate the timer whenever its query range changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataDialog.deviceId, activeRange, appliedStart, appliedEnd, autoRefresh, rawRowLimit, quickRangeMinutes]);

  const axes = {
    flowRate: buildAxisRange(points.map((point) => point.flowRate), axisInputs.flowRate.min, axisInputs.flowRate.max),
    waterLevel: buildAxisRange(points.map((point) => point.waterLevel), axisInputs.waterLevel.min, axisInputs.waterLevel.max),
    velocity: buildAxisRange(points.map((point) => point.velocity), axisInputs.velocity.min, axisInputs.velocity.max),
  };

  const chartData = useMemo(
    () => ({
      labels: points.map((point) => quickRangeMinutes === null ? point.label : point.timestamp),
      datasets: SERIES.map((series) => ({
        label: series.label,
        data: points.map((point) => point[series.key]),
        hidden: hiddenSeries[series.key],
        fill: false,
        borderColor: series.color,
        backgroundColor: series.color,
        borderWidth: 3,
        pointRadius: points.length > 80 ? 0 : 2,
        pointHoverRadius: 4,
        lineTension: 0,
        spanGaps: true,
        yAxisID: `y-${series.key}`,
      })),
    }),
    [hiddenSeries, points, quickRangeMinutes],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      tooltips: {
        mode: 'index' as const,
        intersect: false,
        titleFontSize: 18,
        bodyFontSize: 16,
      },
      hover: { mode: 'nearest' as const, intersect: false },
      scales: {
        xAxes: [
          {
            ...(quickRangeMinutes !== null
              ? {
                  type: 'time',
                  distribution: 'linear',
                  bounds: 'data',
                  time: {
                    min: appliedStart.getTime(),
                    max: appliedEnd.getTime(),
                    unit: 'second',
                    tooltipFormat: 'YYYY/MM/DD HH:mm:ss',
                    displayFormats: { second: 'HH:mm' },
                  },
                }
              : {}),
            gridLines: { color: '#d9e2ef' },
            scaleLabel: {
              display: true,
              labelString: X_AXIS_LABEL[activeRange],
              fontColor: '#475569',
              fontSize: 16,
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 12,
              ...(quickRangeMinutes !== null ? { source: 'data' } : {}),
              maxRotation: 45,
              minRotation: 0,
              fontColor: '#475569',
              fontSize: 16,
            },
          },
        ],
        yAxes: SERIES.map((series, index) => ({
          id: `y-${series.key}`,
          display: !hiddenSeries[series.key],
          position: index === 0 ? ('left' as const) : ('right' as const),
          gridLines: { drawOnChartArea: index === 0, color: '#d9e2ef' },
          scaleLabel: {
            display: true,
            labelString: `${series.label} (${series.unit})`,
            fontColor: series.color,
            fontSize: 16,
          },
          ticks: {
            min: axes[series.key].min,
            max: axes[series.key].max,
            fontColor: series.color,
            fontSize: 16,
          },
        })),
      },
    }),
    [activeRange, appliedEnd, appliedStart, axes.flowRate.max, axes.flowRate.min, axes.velocity.max, axes.velocity.min, axes.waterLevel.max, axes.waterLevel.min, hiddenSeries, quickRangeMinutes],
  );

  const activeAlerts = alertDevice ? configuredAlerts(alertDevice, {
    waterLevel: dataDialog.waterLevel,
    flowRate: dataDialog.flowRate,
    velocity: dataDialog.velocity,
    lastReceivedAt: parsePublishTime(dataDialog.publish)?.getTime() ?? null,
  }, alertCheckedAt) : null;

  const metricCards = [
    { label: '現在の水位', value: toNumber(dataDialog.waterLevel)?.toFixed(2) ?? '--', unit: 'm', tone: 'blue' },
    { label: '現在の流量', value: toNumber(dataDialog.flowRate)?.toFixed(2) ?? '--', unit: 'm³/s', tone: 'green' },
    { label: '現在の流速', value: toNumber(dataDialog.velocity)?.toFixed(2) ?? '--', unit: 'm/s', tone: 'amber' },
    { label: 'アラート件数', description: activeAlerts === null ? 'アラート設定を取得できていません。' : `管理者設定に基づく現在のアラート条件数です。未受信は最終観測日時から判定します。${activeAlerts.length ? activeAlerts.join('／') : '現在該当する条件はありません。'}`, value: activeAlerts === null ? '--' : String(activeAlerts.length), unit: '件', tone: 'amber' },
  ];

  const openInNewWindow = () => {
    const width = 760;
    const height = Math.min(1000, window.screen.availHeight - 40);
    const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));
    window.open(
      `/device-panel/${encodeURIComponent(dataDialog.deviceId)}`,
      '_blank',
      `popup=yes,resizable=yes,scrollbars=yes,width=${width},height=${height},left=${left},top=${top}`,
    );
  };

  const loadMoreRawRows = async () => {
    if (isLoadingMoreRawRows || !hasMoreRawRows) return;
    const nextLimit = rawRowLimit + RAW_DATA_PAGE_SIZE;
    const requestId = requestIdRef.current;
    setIsLoadingMoreRawRows(true);
    try {
      const rawPage = await queryRawRows(nextLimit);
      if (requestId !== requestIdRef.current) return;
      setRawRows(buildRawRows(rawPage.items));
      setRawRowLimit(nextLimit);
      setHasMoreRawRows(rawPage.hasMore);
    } catch (error) {
      console.error(error);
      toasting({ children: '生データの追加取得に失敗しました。', containerProps: { className: 'border-red-600' } });
    } finally {
      setIsLoadingMoreRawRows(false);
    }
  };

  return (
      <aside
        ref={panelRef}
        className={`device-detail-panel ${isActive ? 'is-active' : ''} ${layoutMode === 'embedded' ? 'is-embedded' : ''}`}
        aria-label={`${dataDialog.pos}の詳細`}
        style={
          layoutMode === 'floating'
            ? {
                left: panelOffset.x,
                top: panelOffset.y,
                ...(panelSize ? { width: panelSize.width, height: panelSize.height } : {}),
              }
            : undefined
        }
        onPointerDown={layoutMode === 'floating' ? onActivate : undefined}>
        <header
          className="device-detail-header"
          onPointerDown={layoutMode === 'floating' ? startDragging : undefined}
          onPointerMove={layoutMode === 'floating' ? movePanel : undefined}
          onPointerUp={layoutMode === 'floating' ? stopDragging : undefined}
          onPointerCancel={layoutMode === 'floating' ? stopDragging : undefined}>
          <div>
            <h2 className="device-detail-title">{dataDialog.pos || dataDialog.deviceId}</h2>
            <p className="device-detail-subtitle">{dataDialog.areaName || '対象エリア'}</p>
            <p className="device-detail-meta">更新日時 {dataDialog.publish || '未取得'}</p>
          </div>
          <div className="device-detail-headerActions">
            {layoutMode === 'floating' && (
              <button
                type="button"
                className="device-detail-openWindow"
                aria-label="別ウィンドウで開く"
                title="別ウィンドウで開く"
                onClick={openInNewWindow}>
                <AiOutlineExport size={20} />
              </button>
            )}
            {onClose && (
              <button type="button" className="device-detail-close" aria-label="閉じる" onClick={onClose}>
                <AiOutlineClose size={18} />
              </button>
            )}
          </div>
        </header>

        <div className="device-detail-body">
          {showKpi && (
            <section className="device-detail-section">
              <h3 className="device-detail-sectionTitle">KPI</h3>
              <div className="device-kpi-grid">
                {metricCards.map((card) => (
                  <div key={card.label} className={`device-kpi-card device-kpi-${card.tone}`}>
                    <div className="device-kpi-label" title={card.description}>{card.label}</div>
                    <div className={`device-kpi-valueRow ${getMetricValueSizeClass(card.value)}`}>
                      <strong>{card.value}</strong>
                      <span>{card.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="device-detail-section">
            <h3 className="device-detail-sectionTitle">トレンドグラフ</h3>
            <div className="device-trend-card">
              <div className="device-trend-periodControls">
                <div className="device-trend-tabs">
                  {RANGE_OPTIONS.map((option) => (
                    <button key={option.key} type="button" className={quickRangeMinutes === null && activeRange === option.key ? 'is-active' : ''} onClick={() => selectPreset(option.key)}>
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="device-trend-quickRanges" aria-label="直近の表示範囲">
                  {[5, 10, 30].map((minutes) => (
                    <button key={minutes} type="button" className={quickRangeMinutes === minutes ? 'is-active' : ''} onClick={() => selectRecentMinutes(minutes)}>
                      過去{minutes}分
                    </button>
                  ))}
                </div>
              </div>

              <div className="device-trend-rangeEditor">
                <label>
                  <span>開始</span>
                  <input type="datetime-local" value={startDraft} onChange={(event) => updateDraftRange('start', event.target.value)} />
                </label>
                <label>
                  <span>終了</span>
                  <input type="datetime-local" value={endDraft} onChange={(event) => updateDraftRange('end', event.target.value)} />
                </label>
                <div className="device-trend-actions">
                  <button type="button" onClick={applyRange}>適用</button>
                  <button type="button" onClick={() => void downloadCsv()}>CSV</button>
                  <button type="button" aria-label="再読み込み" title="再読み込み" onClick={() => refreshTrend()}>
                    <AiOutlineReload className={isLoading ? 'device-spin' : ''} size={20} />
                  </button>
                  <button
                    type="button"
                    className={autoRefresh ? 'device-auto-refresh is-enabled' : 'device-auto-refresh'}
                    aria-pressed={autoRefresh}
                    title="30秒ごとの自動更新"
                    onClick={() => setAutoRefresh((current) => !current)}>
                    自動更新 {autoRefresh ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
              {rangeError && <p className="device-trend-error">{rangeError}</p>}

              <button type="button" className="device-axis-toggle" onClick={() => setShowAxisEditor((current) => !current)}>
                {showAxisEditor ? '表示範囲を閉じる<' : '>Y軸の表示範囲を設定'}
              </button>

              {showAxisEditor && (
                <div className="device-axis-editor">
                  {SERIES.map((series) => (
                    <div key={series.key} className="device-axis-row">
                      <span style={{ color: series.color }}>{series.label}</span>
                      <input type="number" step="any" placeholder="自動 Min" value={axisInputs[series.key].min} onChange={(event) => setAxisInputs((current) => ({ ...current, [series.key]: { ...current[series.key], min: event.target.value } }))} />
                      <span>–</span>
                      <input type="number" step="any" placeholder="自動 Max" value={axisInputs[series.key].max} onChange={(event) => setAxisInputs((current) => ({ ...current, [series.key]: { ...current[series.key], max: event.target.value } }))} />
                    </div>
                  ))}
                  <button type="button" className="device-axis-reset" onClick={() => setAxisInputs({ flowRate: { min: '', max: '' }, waterLevel: { min: '', max: '' }, velocity: { min: '', max: '' } })}>
                    自動範囲に戻す
                  </button>
                </div>
              )}

              <div className="device-trend-legend">
                {SERIES.map((series) => (
                  <button key={series.key} type="button" className={hiddenSeries[series.key] ? 'is-hidden' : ''} onClick={() => setHiddenSeries((current) => ({ ...current, [series.key]: !current[series.key] }))}>
                    <span style={{ backgroundColor: series.color }} />
                    {series.label}
                  </button>
                ))}
              </div>

              <div className="device-trend-chart">
                {isLoading && <div className="device-trend-loading">読み込み中...</div>}
                {!isLoading && !points.length && <div className="device-trend-empty">指定した期間のデータがありません。</div>}
                <Line redraw data={chartData} options={chartOptions as any} />
              </div>
              <p className="device-trend-caption">
                表示範囲: {format(appliedStart, quickRangeMinutes === null ? 'yyyy/MM/dd HH:mm' : 'yyyy/MM/dd HH:mm:ss')} - {format(appliedEnd, quickRangeMinutes === null ? 'yyyy/MM/dd HH:mm' : 'yyyy/MM/dd HH:mm:ss')}
              </p>
              <section className="device-raw-data" aria-label="生データ一覧">
                <div className="device-raw-data-heading">
                  <h4>生データ</h4>
                  <span>{rawRowLimit > RAW_DATA_PAGE_SIZE ? `最新${rawRows.length}件` : '最新5件'}</span>
                </div>
                <div className="device-raw-data-scroll">
                  <table>
                    <colgroup>
                      <col className="device-raw-data-timeColumn" />
                      <col />
                      <col />
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>時間</th>
                        <th><span>流量</span><small>m³/s</small></th>
                        <th><span>水位</span><small>m</small></th>
                        <th><span>流速</span><small>m/s</small></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rawRows.map((row) => (
                        <tr key={row.timestamp}>
                          <td>
                            <time dateTime={new Date(row.timestamp).toISOString()}>
                              <span>{format(row.timestamp, 'yyyy/MM/dd')}</span>
                              <span>{format(row.timestamp, 'HH:mm:ss')}</span>
                            </time>
                          </td>
                          <td>{row.flowRate === null ? '--' : row.flowRate.toFixed(4)}</td>
                          <td>{row.waterLevel === null ? '--' : row.waterLevel.toFixed(4)}</td>
                          <td>{row.velocity === null ? '--' : row.velocity.toFixed(4)}</td>
                        </tr>
                      ))}
                      {!rawRows.length && (
                        <tr>
                          <td colSpan={4} className="device-raw-data-empty">生データがありません。</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {hasMoreRawRows && (
                  <button
                    type="button"
                    className="device-raw-data-loadMore"
                    disabled={isLoadingMoreRawRows}
                    onClick={() => void loadMoreRawRows()}>
                    {isLoadingMoreRawRows ? '読み込み中...' : 'さらに5件読み込む'}
                  </button>
                )}
              </section>
            </div>
          </section>
        </div>
        {layoutMode === 'floating' &&
          RESIZE_DIRECTIONS.map((direction) => (
            <span
              key={direction}
              role="presentation"
              className={`device-detail-resizeHandle device-detail-resizeHandle-${direction}`}
              onPointerDown={startResizing(direction)}
              onPointerMove={resizePanel}
              onPointerUp={stopResizing}
              onPointerCancel={stopResizing}
            />
          ))}
      </aside>
  );
}

export function DialogWaterLevel() {
  const dispatch = useDispatch();
  const panels = useSelector((state: RootState) => state.mapDevicePanelsReducer.panels);
  const [activePanelId, setActivePanelId] = useState('');

  useEffect(() => {
    if (panels[0]?.deviceId) setActivePanelId(panels[0].deviceId);
  }, [panels[0]?.deviceId]);

  if (!panels.length) return null;

  return (
    <div className="device-detail-overlay" onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
      {panels.length > 1 && (
        <div className="device-detail-multiActions">
          <span>{panels.length}台を表示中</span>
          <button type="button" onClick={() => dispatch(closeAllMapDevicePanels())}>
            すべて閉じる
          </button>
        </div>
      )}
      <div className="device-detail-grid">
        {panels.map((panel) => (
          <DeviceDetailPanel
            key={panel.deviceId}
            dataDialog={panel}
            isActive={activePanelId === panel.deviceId}
            onActivate={() => setActivePanelId(panel.deviceId)}
            onClose={() => dispatch(closeMapDevicePanel(panel.deviceId))}
          />
        ))}
      </div>
    </div>
  );
}
